import { useEffect, useId, useRef, useState } from "react";
import { useAnalytics } from "../../../lib/analytics";
import { useClientTranslations } from "../../../lib/i18n/client";
import type { OnboardingAnswers } from "../../../types/onboarding";
import styles from "./NeedsMostQuestion.module.css";

interface NeedsMostQuestionProps {
	answers: OnboardingAnswers;
	onInputChange: (field: string, value: string | string[]) => void;
	showValidation?: boolean;
}

export function NeedsMostQuestion({
	answers,
	onInputChange,
	showValidation = false,
}: NeedsMostQuestionProps) {
	const { t, direction } = useClientTranslations("onboarding");
	const { logOnboarding } = useAnalytics();
	const groupId = useId();
	const hasInitialized = useRef(false);
	// Fire the "Other" text event once per visit, not per keystroke.
	const otherTracked = useRef(false);

	const currentSelections = Array.isArray(answers.needsMost)
		? answers.needsMost
		: [];

	// Extract existing "Other - ..." text if present
	const existingOtherEntry = currentSelections.find((v) =>
		v.startsWith("Other - "),
	);
	const initialOtherText = existingOtherEntry
		? existingOtherEntry.replace("Other - ", "")
		: "";

	const [otherText, setOtherText] = useState(initialOtherText);

	// Initialize otherText from existing data on mount
	useEffect(() => {
		if (!hasInitialized.current && initialOtherText) {
			setOtherText(initialOtherText);
			hasInitialized.current = true;
		}
	}, [initialOtherText]);

	const handleToggleOption = (value: string) => {
		// Remove any existing "Other" or "Other - ..." entries
		const selectionsWithoutOther = currentSelections.filter(
			(v) => v !== "Other" && !v.startsWith("Other - "),
		);

		const isCurrentlySelected =
			currentSelections.includes(value) ||
			currentSelections.some((v) => v.startsWith("Other - "));

		let newSelections: string[];
		if (value === "Other") {
			if (isCurrentlySelected) {
				// Deselecting "Other"
				newSelections = selectionsWithoutOther;
				setOtherText("");
			} else {
				// Selecting "Other"
				newSelections = [...selectionsWithoutOther, value];
			}
		} else {
			// Toggle non-Other options normally
			newSelections = currentSelections.includes(value)
				? currentSelections.filter((v) => v !== value)
				: [...currentSelections, value];
		}

		onInputChange("needsMost", newSelections);

		logOnboarding(isCurrentlySelected ? "needs_deselected" : "needs_selected", {
			field_name: "needsMost",
			// The option just toggled…
			selected_value: value,
			// …and the full current selection, so a single event shows everything
			// chosen (break down by `selected_values` in PostHog).
			selected_values: newSelections,
			total_selected: newSelections.length,
		});
	};

	const handleOtherTextChange = (text: string) => {
		setOtherText(text);

		// Update the array: remove old "Other" or "Other - ..." and add new "Other - <text>"
		const selectionsWithoutOther = currentSelections.filter(
			(v) => v !== "Other" && !v.startsWith("Other - "),
		);

		const newValue = text.trim() ? `Other - ${text.trim()}` : "Other";
		const newSelections = [...selectionsWithoutOther, newValue];

		onInputChange("needsMost", newSelections);

		if (text.trim() && !otherTracked.current) {
			otherTracked.current = true;
			logOnboarding("needs_other_text_entered", { field_name: "needsMost" });
		}
	};

	const hasError = showValidation && currentSelections.length === 0;

	const needsOptions = [
		{
			value: "Professional guidance",
			label: t("needsMost.options.professionalGuidance"),
		},
		{
			value: "Emotional support",
			label: t("needsMost.options.emotionalSupport"),
		},
		{
			value: "Providers review",
			label: t("needsMost.options.providersReview"),
		},
		{ value: "A community", label: t("needsMost.options.community") },
		{
			value: "Planning my journey",
			label: t("needsMost.options.planningJourney"),
		},
		{ value: "Other", label: t("needsMost.options.other") },
	];

	return (
		<div className={styles.container} dir={direction}>
			<fieldset className={styles.optionsList} id={groupId}>
				{needsOptions.map((option) => {
					const isSelected =
						option.value === "Other"
							? currentSelections.includes("Other") ||
								currentSelections.some((v) => v.startsWith("Other - "))
							: currentSelections.includes(option.value);
					return (
						<label
							key={option.value}
							className={`${styles.optionCard} ${
								isSelected ? styles.optionCardSelected : ""
							} ${hasError ? styles.optionCardError : ""}`}
						>
							<input
								type="checkbox"
								checked={isSelected}
								onChange={() => handleToggleOption(option.value)}
								className={styles.hiddenCheckbox}
								aria-label={option.label}
							/>
							<div
								className={`${styles.checkbox} ${
									isSelected ? styles.checkboxSelected : ""
								}`}
							>
								{isSelected && (
									<svg
										width="12"
										height="9"
										viewBox="0 0 12 9"
										fill="none"
										xmlns="http://www.w3.org/2000/svg"
										className={styles.checkmark}
										role="img"
										aria-label="Selected"
									>
										<path
											d="M1 4.5L4.5 8L11 1"
											stroke="white"
											strokeWidth="2"
											strokeLinecap="round"
											strokeLinejoin="round"
										/>
									</svg>
								)}
							</div>
							<span className={styles.optionLabel}>{option.label}</span>
						</label>
					);
				})}
			</fieldset>
			{(currentSelections.includes("Other") ||
				currentSelections.some((v) => v.startsWith("Other - "))) && (
				<div className={styles.otherInputWrapper}>
					<input
						type="text"
						value={otherText}
						onChange={(e) => handleOtherTextChange(e.target.value)}
						placeholder={t("needsMost.otherPlaceholder")}
						className={styles.otherInput}
						aria-label={t("needsMost.otherPlaceholder")}
					/>
				</div>
			)}
		</div>
	);
}
